import { useEffect, useState } from 'react';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import LoginPage from './components/pages/LoginPage';
import SignUpPage from './components/pages/SignUpPage';
import OtpPage from './components/pages/OtpPage';
import HomePage from './components/pages/HomePage';
import CategoriesPage from './components/pages/CategoriesPage';
import InquiryPage from './components/pages/InquiryPage';
import SupportPage from './components/pages/SupportPage';
import ProductDetailsPage from './components/pages/ProductDetailsPage';
import ProfilePage from './components/pages/ProfilePage';
import CheckoutPage from './components/pages/CheckoutPage';
import CheckoutInquiryPage from './components/pages/CheckoutInquiryPage';
import InquirySuccessPage from './components/pages/InquirySuccessPage';
import OrderSuccessPage from './components/pages/OrderSuccessPage';
import OrdersPage from './components/pages/OrdersPage';
import OrderDetailsPage from './components/pages/OrderDetailsPage';
import FavoritesPage from './components/pages/FavoritesPage';
import FaqPage from './components/pages/FaqPage';
import ProductsPage from './components/pages/ProductsPage';
import CartPage from './components/pages/CartPage';
import AddressesPage from './components/pages/AddressesPage';
import AboutPage from './components/pages/AboutPage';
import PrivacyPage from './components/pages/PrivacyPage';
import SearchResultsPage from './components/pages/SearchResultsPage';
import TrackingPage from './components/pages/TrackingPage';
import { isLoggedIn, isGuestSession, disableGuestMode } from './services/auth';
import { LanguageProvider } from './context/LanguageContext';
import './App.css';

const renderLoginPage = () => (
  <LanguageProvider>
    <LoginPage />
  </LanguageProvider>
);

function App() {
  const canUseBrowser = typeof window !== 'undefined';
  const getPath = () =>
    canUseBrowser ? window.location.pathname.toLowerCase() : '/';
  const [pathname, setPathname] = useState(getPath);

  useEffect(() => {
    if (!canUseBrowser) return undefined;
    const handlePopState = () => {
      setPathname(window.location.pathname.toLowerCase());
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [canUseBrowser]);
  const isLoginRoute = pathname.includes('login');
  const isSignupRoute = pathname.includes('signup');
  const isOtpRoute = pathname.includes('otp');
  const isAuthRoute = isLoginRoute || isSignupRoute || isOtpRoute;
  const isCategoriesRoute = pathname.includes('categories');
  const isCheckoutInquiryRoute = pathname.includes('checkout-inquiry');
  const isCheckoutRoute =
    pathname.includes('checkout') && !isCheckoutInquiryRoute;
  const isInquirySuccessRoute = pathname.includes('inquiry-success');
  const isOrderSuccessRoute = pathname.includes('order-success');
  const isOrderDetailsRoute = pathname.includes('order-details');
  const isOrdersRoute =
    pathname.includes('orders') &&
    !isOrderSuccessRoute &&
    !isOrderDetailsRoute;
  const isFavoritesRoute = pathname.includes('favorites');
  const isProductsRoute =
    pathname.startsWith('/products') && !pathname.startsWith('/product/');
  const isFaqRoute = pathname.includes('faq');
  const isInquiryRoute =
    pathname.includes('inquiry') &&
    !isInquirySuccessRoute &&
    !isCheckoutRoute &&
    !isCheckoutInquiryRoute;
  const isSupportRoute = pathname.includes('support');
  const isCartRoute = pathname.includes('cart');
  const isAddressesRoute = pathname.includes('addresses');
  const isSearchRoute = pathname.includes('search');
  const isAboutRoute = pathname.includes('about');
  const isPrivacyRoute = pathname.includes('privacy');
  const isTrackingRoute = pathname.includes('tracking');
  const isProductRoute =
    pathname.startsWith('/product/') && !pathname.startsWith('/products');
  const isProfileRoute = pathname.includes('profile');
  const isPublicInfoRoute = isAboutRoute || isPrivacyRoute || isTrackingRoute;
  const loggedIn = isLoggedIn();
  const guestMode = isGuestSession();

  const redirectTo = (path, { replace = false } = {}) => {
    if (!canUseBrowser) return;
    if (window.location.pathname === path) return;
    if (replace) {
      window.history.replaceState(null, '', path);
    } else {
      window.history.pushState(null, '', path);
    }
    setPathname(path.toLowerCase());
  };

  if (
    !loggedIn &&
    !guestMode &&
    !isAuthRoute &&
    !isPublicInfoRoute
  ) {
    redirectTo('/login', { replace: true });
    return renderLoginPage();
  }

  if (canUseBrowser && guestMode && isAuthRoute && !isSignupRoute) {
    // allow guest to access signup if they decide to create account
    disableGuestMode();
    redirectTo('/login', { replace: true });
    return renderLoginPage();
  }

  if (canUseBrowser && loggedIn && isAuthRoute) {
    redirectTo('/', { replace: true });
    return null;
  }

  if (isLoginRoute) {
    return renderLoginPage();
  }
  if (isSignupRoute) {
    return (
      <LanguageProvider>
        <SignUpPage />
      </LanguageProvider>
    );
  }
  if (isOtpRoute) {
    return (
      <LanguageProvider>
        <OtpPage />
      </LanguageProvider>
    );
  }

  if (isTrackingRoute) {
    return (
      <LanguageProvider>
        <div className="tracking-shell">
          <main className="tracking-shell__main">
            <TrackingPage />
          </main>
        </div>
      </LanguageProvider>
    );
  }

  const PageComponent = isProductRoute
    ? ProductDetailsPage
    : isProfileRoute
    ? ProfilePage
    : isInquirySuccessRoute
    ? InquirySuccessPage
    : isOrderSuccessRoute
    ? OrderSuccessPage
    : isOrderDetailsRoute
    ? OrderDetailsPage
    : isCheckoutInquiryRoute
    ? CheckoutInquiryPage
    : isCheckoutRoute
    ? CheckoutPage
    : isOrdersRoute
    ? OrdersPage
    : isCartRoute
    ? CartPage
    : isFavoritesRoute
    ? FavoritesPage
    : isAboutRoute
    ? AboutPage
    : isPrivacyRoute
    ? PrivacyPage
    : isSearchRoute
    ? SearchResultsPage
    : isAddressesRoute
    ? AddressesPage
    : isProductsRoute
    ? ProductsPage
    : isFaqRoute
    ? FaqPage
    : isSupportRoute
    ? SupportPage
    : isInquiryRoute
    ? InquiryPage
    : isCategoriesRoute
    ? CategoriesPage
    : HomePage;

  return (
    <LanguageProvider>
      <div className="app-shell">
        <Header />
        <main>
          <PageComponent />
        </main>
        <Footer />
      </div>
    </LanguageProvider>
  );
}

export default App;
